import { LinkedText } from '~/util/linked-text'
import { PaperClipIcon } from '@st/theme'
import { useProcess } from '~/store'
import type { selClientTaxPayments } from './client-organizer-selectors'
import { ClientCard, ClientCardStepHeading } from './ui'

type TaxPaymentsModel = NonNullable<ReturnType<typeof selClientTaxPayments>>

export function ClientTaxPayments({
  stepNumber,
  model
}: {
  stepNumber: number
  model: TaxPaymentsModel
}) {
  const folder = useProcess('stFolder')
  const route = useProcess('route')
  return (
    <ClientCard>
      <ClientCardStepHeading
        stepNumber={stepNumber}
        completed={model.completed}
        status={<span className="text-sm">Check each item once you’ve completed it.</span>}
        title={<h2 className="text-xl font-semibold text-gray-900">Pay taxes</h2>}
      />
      {model.items.map((item) => {
        return (
          <div
            key={item.id}
            className="mt-5 grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2 border-t border-gray-200 pt-5 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
          >
            <input
              type="checkbox"
              aria-label={`Complete ${item.name}`}
              checked={item.status == 'complete'}
              disabled={model.archived}
              className="mt-1 size-4 rounded border-gray-300 text-green-600 focus:ring-green-600"
              onChange={() =>
                folder.send({
                  type: 'checklist',
                  message: { type: 'completionToggled', item }
                })
              }
            />
            <div className="min-w-0">
              <h3 className="text-base font-medium text-gray-900">{item.name}</h3>
              {item.note && (
                <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-gray-500">
                  <LinkedText text={item.note} />
                </p>
              )}
              {item.documents.map((doc) => {
                return (
                  <button
                    key={doc.id}
                    className="mt-2 flex items-center gap-2 text-sm text-blue-500 hover:underline"
                    onClick={() => route.send({ type: 'openUrl', url: doc.urls.view! })}
                  >
                    <PaperClipIcon className="size-4 shrink-0" />
                    {doc.name}
                  </button>
                )
              })}
            </div>
            <div className="col-start-2 flex flex-wrap items-baseline gap-x-3 sm:col-start-3 sm:row-start-1 sm:block sm:text-right">
              {item.amountLabel && (
                <p className="text-lg font-semibold tabular-nums text-gray-900">
                  {item.amountLabel}
                </p>
              )}
              {item.dueLabel && <p className="mt-1 text-xs text-gray-500">Due {item.dueLabel}</p>}
            </div>
          </div>
        )
      })}
    </ClientCard>
  )
}
